import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { maskCPF, signerMissingFields } from "../lib/representatives";
import { Button } from "./ui/button";
import RepresentativeFormDialog from "./RepresentativeFormDialog";
import {
  AlertTriangle, Archive, ArchiveRestore, Loader2, PenLine, Plus, UserRound,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Seção "Sócios / Representantes" do detalhe do cedente (item 22).
 *
 * Quem assina os contratos ZapSign do cedente PJ são os representantes (1 a 7
 * por operação); a gestão do cadastro é do PCP. Regras espelhadas da V2:
 * documento travado (arquive e recadastre), arquivamento reversível, flag
 * "assinante padrão" pré-seleciona no wizard.
 */
export default function RepresentativesSection({ cedenteId, canManage }) {
  const [reps, setReps] = useState(null); // null = carregando
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState({ open: false, rep: null });
  const [mutatingId, setMutatingId] = useState(null);

  // Descarta resposta fora de ordem do load (mesmo padrão do signersSeqRef do
  // wizard): mount, onSaved do dialog e setStatus concorrem — uma lista velha
  // nunca sobrescreve a fresca (ex.: representante recém-criado "sumindo").
  const loadSeqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setError(null);
    try {
      const { data } = await api.get(`/v2/cedentes/${cedenteId}/representatives`, {
        params: { status: "all", limit: 200 },
      });
      if (seq !== loadSeqRef.current) return; // resposta superada: descarta
      setReps(data.items || []);
    } catch (err) {
      if (seq !== loadSeqRef.current) return; // resposta superada: descarta
      setReps([]);
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    }
  }, [cedenteId]);

  useEffect(() => {
    setReps(null);
    load();
    // Troca de cedente/unmount: invalida a resposta em voo da carga anterior.
    return () => { loadSeqRef.current += 1; };
  }, [load]);

  const setStatus = async (rep, status) => {
    setMutatingId(rep.id);
    try {
      await api.patch(`/v2/cedentes/${cedenteId}/representatives/${rep.id}`, { status });
      toast.success(
        status === "archived"
          ? `${rep.full_name} arquivado — reversível em "arquivados".`
          : `${rep.full_name} reativado.`
      );
      load();
    } catch (err) {
      // Ex.: 409 da V2 ao reativar com outro ATIVO de mesmo CPF — exibido intacto.
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setMutatingId(null);
    }
  };

  const active = (reps || []).filter((r) => r.status === "active");
  const archived = (reps || []).filter((r) => r.status === "archived");

  return (
    <div data-testid="representatives-section">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold">Sócios / Representantes</h4>
          <p className="text-[12px] text-zinc-500">
            Quem pode assinar contratos pelo cedente (1 a 7 por operação).
          </p>
        </div>
        {canManage && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => setDialog({ open: true, rep: null })}
            data-testid="add-representative-btn"
          >
            <Plus className="h-3.5 w-3.5" /> Adicionar
          </Button>
        )}
      </div>

      {reps === null && (
        <div className="flex items-center gap-2 text-sm text-zinc-500 py-4" data-testid="representatives-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando representantes...
        </div>
      )}

      {reps !== null && error && (
        <div role="alert" className="rounded-md p-3 border border-red-200 bg-red-50 text-[13px] text-red-800 space-y-1" data-testid="representatives-error">
          <div>{error}</div>
          <button onClick={load} className="underline font-medium">Tentar novamente</button>
        </div>
      )}

      {reps !== null && !error && (
        <>
          <div className="border border-[var(--zemo-border)] rounded-md divide-y divide-[var(--zemo-border)]">
            {active.map((rep) => {
              const missing = signerMissingFields(rep);
              return (
                <div key={rep.id} className="px-3 py-2.5" data-testid={`representative-row-${rep.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-zinc-900">{rep.full_name}</span>
                        {rep.is_default_signer && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#0055FF]/10 text-[#0055FF] border border-[#0055FF]/20">
                            Assinante padrão
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-zinc-500 font-mono-num">{maskCPF(rep.document_number)}</div>
                      <div className="text-[12px] text-zinc-500 truncate">
                        {rep.email}{rep.phone ? ` · ${rep.phone}` : ""}
                      </div>
                      {missing.length > 0 && (
                        <div className="mt-1 flex items-start gap-1.5 text-[12px] text-amber-700" data-testid={`representative-incomplete-${rep.id}`}>
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          <span>
                            Cadastro incompleto para assinar contratos — falta: {missing.join(", ")}.
                          </span>
                        </div>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          title="Editar"
                          aria-label="Editar representante"
                          className="p-1.5 rounded text-zinc-400 hover:text-[#0055FF] hover:bg-[#0055FF]/5 transition-colors"
                          onClick={() => setDialog({ open: true, rep })}
                          data-testid={`edit-representative-${rep.id}`}
                        >
                          <PenLine className="h-4 w-4" />
                        </button>
                        <button
                          title="Arquivar (reversível)"
                          aria-label="Arquivar representante"
                          className="p-1.5 rounded text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                          disabled={mutatingId === rep.id}
                          onClick={() => setStatus(rep, "archived")}
                          data-testid={`archive-representative-${rep.id}`}
                        >
                          {mutatingId === rep.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {active.length === 0 && (
              <div className="px-3 py-6 text-center text-zinc-500 text-sm" data-testid="representatives-empty">
                <UserRound className="h-5 w-5 mx-auto mb-1.5 text-zinc-300" />
                Nenhum representante ativo. Operações de cedente PJ exigem ao
                menos um representante assinante — cadastre para poder operar.
              </div>
            )}
          </div>

          {archived.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setShowArchived((v) => !v)}
                className="text-[12px] text-zinc-500 hover:text-zinc-800 underline"
                data-testid="toggle-archived-representatives"
              >
                {showArchived ? "Ocultar arquivados" : `Mostrar arquivados (${archived.length})`}
              </button>
              {showArchived && (
                <div className="mt-2 border border-dashed border-[var(--zemo-border)] rounded-md divide-y divide-[var(--zemo-border)] bg-zinc-50/40">
                  {archived.map((rep) => (
                    <div key={rep.id} className="px-3 py-2.5 flex items-center justify-between gap-2" data-testid={`archived-representative-${rep.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-500 line-through">{rep.full_name}</div>
                        <div className="text-[12px] text-zinc-400 font-mono-num">{maskCPF(rep.document_number)}</div>
                      </div>
                      {canManage && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5 shrink-0"
                          disabled={mutatingId === rep.id}
                          onClick={() => setStatus(rep, "active")}
                          data-testid={`restore-representative-${rep.id}`}
                        >
                          {mutatingId === rep.id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <ArchiveRestore className="h-3.5 w-3.5" />}
                          Reativar
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <RepresentativeFormDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        cedenteId={cedenteId}
        representative={dialog.rep}
        onSaved={load}
      />
    </div>
  );
}
